/**
 * Composite CRM primary-key helpers: normalized keys must match connector APIs
 * (`/connector/hubspot/search`, `/connector/salesforce/mapped-upsert/preview`).
 *
 * **Multi-column:** After trimming + lowercasing each part, the canonical key is
 * `JSON.stringify(parts)` so values cannot collide with an internal delimiter.
 * **Single-column:** The canonical key is still the single normalized string (same as legacy).
 *
 * Backends must use this exact scheme for composite keys in preview/search responses.
 */

export function normalizedCompositePrimaryKeyParts(
  values: unknown[]
): string[] {
  return values.map((v) =>
    String(v ?? '')
      .trim()
      .toLowerCase()
  );
}

/**
 * Stable string key for Sets/maps and for matching API `results` / `not_found` keys.
 */
export function canonicalCompositeKeyFromNormalizedParts(
  parts: string[]
): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return JSON.stringify(parts);
}

export function normalizedCompositeKeyFromRow(
  row: Record<string, unknown>,
  pkSourceColumns: string[]
): string {
  const parts = normalizedCompositePrimaryKeyParts(
    pkSourceColumns.map((col) => row[col])
  );
  return canonicalCompositeKeyFromNormalizedParts(parts);
}

export function rowHasAllPkValues(
  row: Record<string, unknown>,
  pkSourceColumns: string[]
): boolean {
  return pkSourceColumns.every((col) => {
    const v = row[col];
    return v != null && String(v).trim() !== '';
  });
}

/**
 * If the key is a JSON array, as in the case of multiple primary key columns,
 * return a comma-separated list of the values. Otherwise, return the key unchanged.
 */
export function formatCanonicalCompositeKeyForDisplay(
  canonicalKey: string
): string {
  const key = canonicalKey.trim();

  if (key.startsWith('[') && key.endsWith(']')) {
    const parsed = JSON.parse(key) as unknown;
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((x) => String(x)).join(', ');
    }
    const inner = key.slice(1, -1).trim();
    if (inner.length > 0) {
      const segments = inner.split(/,\s*/);
      const parts: string[] = [];
      for (const seg of segments) {
        const s = seg.trim();
        if (
          (s.startsWith("'") && s.endsWith("'")) ||
          (s.startsWith('"') && s.endsWith('"'))
        ) {
          parts.push(s.slice(1, -1).replace(/\\(.)/g, '$1'));
        } else {
          parts.push(s);
        }
      }
      if (parts.length > 0) {
        return parts.join(', ');
      }
    }
  }

  return key;
}

/** Human-readable label for conflict modal (original cell values). */
export function formatCompositePkDisplay(
  row: Record<string, unknown>,
  pkSourceColumns: string[]
): string {
  return pkSourceColumns
    .map((col) => String(row[col] ?? '').trim())
    .filter((s) => s !== '')
    .join(', ');
}

export function orderedPkSourceColumns(
  mappings: Array<{
    tableColumn: string;
    crmField: string | null;
    isPrimaryKey?: boolean;
  }>,
  columnIndexByKey: Map<string, number>
): string[] {
  const pk = mappings.filter((m) => m.crmField && m.isPrimaryKey);
  pk.sort(
    (a, b) =>
      (columnIndexByKey.get(a.tableColumn) ?? 0) -
      (columnIndexByKey.get(b.tableColumn) ?? 0)
  );
  return pk.map((m) => m.tableColumn);
}

export function pkSourceColumnsFromConnectorFields<
  T extends { source_col: string; use_for_search: boolean; order: number }
>(fields: T[]): string[] {
  return fields
    .filter((f) => f.use_for_search)
    .sort((a, b) => a.order - b.order)
    .map((f) => f.source_col);
}
